import type { FC, PropsWithChildren } from 'react';

import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ObservedScrollAreaContext } from './ObservedScrollAreaContext';

type Position = {
  left: number;
  top: number;
};

type ScrollState = {
  headerOffset: number;
  left?: number;
  setScrollPosition: (pos: Position) => void;
  stickAt: number;
  top?: number;
};

const ScrollContext = createContext<ScrollState>({
  headerOffset: 0,
  setScrollPosition: () => void 0,
  stickAt: 0,
});
ScrollContext.displayName = 'ScrollContext';

const useScrollContext = () => useContext(ScrollContext);

type Props = {
  headerOffset?: number;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  stickAt?: number;
};

/**
 * Provides a context for the scroll position of the element.
 */
const ScrollProvider: FC<PropsWithChildren<Props>> = memo(
  ({ children, headerOffset = 0, onScroll: delegateOnScroll, stickAt = 0 }) => {
    const [initScrollPosition, setInitScrollPosition] = useState<Position>();
    const [scrollPosition, setScrollPosition] = useState<ScrollState>({
      headerOffset,
      setScrollPosition: setInitScrollPosition,
      stickAt,
    });
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (initScrollPosition && ref.current) {
        ref.current.scrollTo(initScrollPosition);
      }
    }, [initScrollPosition, ref]);

    useEffect(() => {
      setScrollPosition(prevState => ({ ...prevState, headerOffset }));
    }, [headerOffset]);

    useEffect(() => {
      setScrollPosition(prevState => ({ ...prevState, stickAt }));
    }, [stickAt]);

    useEffect(() => {
      setScrollPosition(prevState => ({ ...prevState, setInitScrollPosition }));
    }, [setInitScrollPosition]);

    const onScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        delegateOnScroll?.(e);
        const elem = e.target as HTMLDivElement;
        setScrollPosition(prevState => ({
          ...prevState,
          left: elem.scrollLeft,
          top: elem.scrollTop,
        }));
      },
      [setScrollPosition, delegateOnScroll],
    );

    const value = useMemo(() => ({ onScroll, ref }), [ref, onScroll]);

    return (
      <ScrollContext.Provider value={scrollPosition}>
        <ObservedScrollAreaContext.Provider value={value}>
          {children}
        </ObservedScrollAreaContext.Provider>
      </ScrollContext.Provider>
    );
  },
);

export { ScrollProvider, useScrollContext };
